import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../axiosInstance';

/**
 * Inbound Airtuerk booking queue (Bookings → Imported Bookings).
 *
 * Mounted at its own top-level path, NOT under /bookings — the bookings router
 * declares `/:id` early, so /bookings/imports would be read as a booking id.
 */
const BASE = '/booking-imports';

/** Only send filters the API understands, and drop the empty ones. */
const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) out[k] = v;
  });
  return out;
};

const messageFrom = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/* ──────────────────────────────────────────────────────────────────────────
   Queue list + counts
   ────────────────────────────────────────────────────────────────────────── */

export const useGetImports = (params = {}) => {
  const { page = 1, limit = 10, ...filters } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Stable dependency so the effect only refires when a value really changed,
  // not on every parent render that rebuilds the params object.
  const paramsKey = JSON.stringify(cleanParams({ page, limit, ...filters }));

  const fetchImports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(BASE, { params: JSON.parse(paramsKey) });
      if (res.data?.success) {
        setData(res.data.data || []);
        if (res.data.pagination) setPagination(res.data.pagination);
      } else {
        setError(res.data?.message || 'Failed to load imported bookings');
        setData([]);
      }
    } catch (err) {
      setError(messageFrom(err, 'Failed to load imported bookings'));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  return { data, pagination, loading, error, refetch: fetchImports };
};

export const useGetImportStatusCounts = (params = {}) => {
  const [data, setData] = useState({ All: 0 });
  const [loading, setLoading] = useState(true);
  const paramsKey = JSON.stringify(cleanParams(params));

  const fetchCounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE}/status-counts`, { params: JSON.parse(paramsKey) });
      if (res.data?.success) setData(res.data.data || { All: 0 });
    } catch {
      // A failed count is cosmetic — the tabs simply show zero rather than
      // blocking the table behind them.
    } finally {
      setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  return { data, loading, refetch: fetchCounts };
};

/* ──────────────────────────────────────────────────────────────────────────
   One import — full parse, warnings, raw XML
   ────────────────────────────────────────────────────────────────────────── */

export const useGetImport = (id) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Guards against a slow response for a row the user already navigated away
  // from overwriting the one they are now looking at.
  const latest = useRef(0);

  const fetchImport = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(`${BASE}/${id}`);
      if (ticket !== latest.current) return;
      if (res.data?.success) setData(res.data.data);
      else setError(res.data?.message || 'Failed to load import');
    } catch (err) {
      if (ticket !== latest.current) return;
      setError(messageFrom(err, 'Failed to load import'));
    } finally {
      if (ticket === latest.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchImport();
  }, [fetchImport]);

  return { data, loading, error, refetch: fetchImport };
};

/* ──────────────────────────────────────────────────────────────────────────
   Actions
   ────────────────────────────────────────────────────────────────────────── */

/** Shared shape for the mutating hooks: { execute, loading }. */
const useAction = (run) => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (...args) => {
      setLoading(true);
      try {
        const res = await run(...args);
        return { success: Boolean(res.data?.success), ...res.data };
      } catch (err) {
        return { success: false, message: messageFrom(err, 'Request failed') };
      } finally {
        setLoading(false);
      }
    },
    [run]
  );

  return { execute, loading };
};

/**
 * Pull everything waiting on Airtuerk's SFTP drop. This is how bookings
 * normally arrive — upload is only for a file that came by another route.
 * Safe to press twice: duplicates are rejected server-side.
 */
export const useSyncFromAirtuerk = () => useAction(() => axiosInstance.post(`${BASE}/sync`, {}, { timeout: 120000 }));

/** Whether the SFTP drop is configured and answering, for the connection hint. */
export const useAirtuerkConnection = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(`${BASE}/connection`)
      .then((res) => {
        if (!cancelled && res.data?.success) setData(res.data.data);
      })
      .catch(() => {
        // Silent: a failed probe just leaves the hint hidden. The Fetch button
        // reports properly when it is actually pressed.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return data;
};

export const useUploadImports = () =>
  useAction((files) => {
    const form = new FormData();
    Array.from(files).forEach((f) => form.append('files', f));
    return axiosInstance.post(`${BASE}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  });

export const useIgnoreImport = () =>
  useAction((id, reason) => axiosInstance.patch(`${BASE}/${id}/ignore`, { reason }));

export const useRestoreImport = () => useAction((id) => axiosInstance.patch(`${BASE}/${id}/restore`));

/**
 * Take a trip off the queue for good, every file of it, for something that
 * should never have arrived. It does not come back on the next fetch. Refused
 * server-side once the trip has become a booking.
 */
export const useRemoveImport = () => useAction((id) => axiosInstance.delete(`${BASE}/${id}`));

/**
 * Re-run the Airtuerk baggage lookup for one import. The file never states an
 * allowance; Airtuerk's fare data does. Also writes it onto the booking the
 * import became, blank slots only.
 */
export const useLookupImportBaggage = () =>
  useAction((id) => axiosInstance.post(`${BASE}/${id}/baggage`, {}, { timeout: 90000 }));

/** Records that an import became a booking, so the queue row stops being actionable. */
export const useLinkImportToBooking = () =>
  useAction((id, bookingId) => axiosInstance.patch(`${BASE}/${id}/link`, { bookingId }));
